
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
  Badge,
  InputGroup,
  Tooltip,
  OverlayTrigger,
  Pagination,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  Eye,
  Home,
  CheckCircle,
  XCircle,
  Users,
  ChevronUp,
  ChevronDown,
  ChevronsUp,
} from "react-feather";
import { toast } from "react-toastify";

import "./HouseMasterPage.css";

import {
  clearHouseMasterError,
  createHouseThunk,
  deleteHouseThunk,
  fetchHouseMasterThunk,
  updateHouseThunk,
} from "../../../store/slices/houseMasterSlice";

import { fetchMeterTypesThunk } from "../../../store/slices/meterTypesSlice";
import { fetchSlrmTypesThunk } from "../../../store/slices/slrmTypesSlice";
import {
  fetchWardsThunk,
  clearWardsError,
} from "../../../store/slices/wardsSlice";

const confirmToast = (message, { onYes, onNo } = {}) => {
  toast(
    ({ closeToast }) => (
      <div>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>
          {message}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => {
              closeToast?.();
              onYes?.();
            }}
          >
            Delete
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              closeToast?.();
              onNo?.();
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    ),
    {
      autoClose: false,
      closeOnClick: false,
      closeButton: false,
      draggable: false,
    },
  );
};

export default function HouseMasterPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    list,
    loading,
    saving,
    error,
    pagination = {},
    stats: serverStats = null,
  } = useSelector((state) => state.houseMaster);

  const meterTypes = useSelector(
    (state) => state.meterTypes?.list || [],
  );
  const meterTypesLoading = useSelector(
    (state) => state.meterTypes?.loading,
  );
  const meterTypesError = useSelector(
    (state) => state.meterTypes?.error,
  );

  const slrmTypes = useSelector(
    (state) => state.slrmTypes?.list || [],
  );
  const slrmTypesLoading = useSelector(
    (state) => state.slrmTypes?.loading,
  );
  const slrmTypesError = useSelector(
    (state) => state.slrmTypes?.error,
  );

  const wards = useSelector(
    (state) => state.wards?.list || [],
  );
  const wardsLoading = useSelector(
    (state) => state.wards?.loading,
  );
  const wardsError = useSelector(
    (state) => state.wards?.error,
  );

  const permissions =
    useSelector(
      (state) => state.permissions?.map?.house_master,
    ) || {};

  const canCreate = Boolean(permissions.can_create);
  const canUpdate = Boolean(permissions.can_update);
  const canDelete = Boolean(permissions.can_delete);
  const canView = permissions.can_view !== false;

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("create");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [connectionFilter, setConnectionFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [sortConfig, setSortConfig] = useState({
    key: "home_id",
    direction: "desc",
  });

  const [form, setForm] = useState({
    home_id: null,
    qr_code: "",
    door_no: "",
    family_head: "",
    mobile: "",
    ward_id: "",
    mtr_type: "",
    slrm_type: "",
    is_water: false,
    is_slrm: false,
    commercial: false,
    waste_collection: false,
    last_bill_date: "",
    slrm_last_bill_date: "",
    slrm_water_together: "no",
    status: "Active",
  });

  const toInputDate = (value) => {
    if (!value) return "";

    const dateValue = String(value).trim();

    const yyyyMmDd = dateValue.match(
      /^(\d{4})-(\d{2})-(\d{2})/,
    );

    if (yyyyMmDd) {
      return `${yyyyMmDd[1]}-${yyyyMmDd[2]}-${yyyyMmDd[3]}`;
    }

    const ddMmYyyy = dateValue.match(
      /^(\d{2})[/-](\d{2})[/-](\d{4})/,
    );

    if (ddMmYyyy) {
      return `${ddMmYyyy[3]}-${ddMmYyyy[2]}-${ddMmYyyy[1]}`;
    }

    return "";
  };

  const fmtDate = (value) => {
    if (!value) return "—";

    const dateValue = String(value).trim();

    if (/^\d{2}-\d{2}-\d{4}$/.test(dateValue)) {
      return dateValue;
    }

    if (/^\d{2}-\d{2}-\d{4}\s+/.test(dateValue)) {
      return dateValue;
    }

    const match = dateValue.match(
      /^(\d{4})-(\d{2})-(\d{2})/,
    );

    if (match) {
      const date = new Date(
        `${match[1]}-${match[2]}-${match[3]}T00:00:00`,
      );

      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString("en-IN", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        });
      }

      return `${match[3]}-${match[2]}-${match[1]}`;
    }

    return dateValue;
  };

  const fmtNum = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "—";
    }

    const numberValue = Number(value);

    return Number.isFinite(numberValue)
      ? numberValue.toString()
      : String(value);
  };

  useEffect(() => {
    dispatch(fetchMeterTypesThunk());
    dispatch(fetchSlrmTypesThunk());
    dispatch(fetchWardsThunk());
  }, [dispatch]);

  // Server-side pagination / search / filters. Search is debounced so the API
  // is not called for every keystroke immediately.
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(
        fetchHouseMasterThunk({
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm.trim(),
          status: statusFilter === "all" ? "" : statusFilter,
          connection: connectionFilter,
        }),
      );
    }, 350);

    return () => clearTimeout(timer);
  }, [
    dispatch,
    currentPage,
    itemsPerPage,
    searchTerm,
    statusFilter,
    connectionFilter,
  ]);

  useEffect(() => {
    if (!error) return;

    toast.error(String(error));
    dispatch(clearHouseMasterError());
  }, [error, dispatch]);

  useEffect(() => {
    if (!wardsError) return;

    toast.error(String(wardsError));
    dispatch(clearWardsError());
  }, [wardsError, dispatch]);

  useEffect(() => {
    if (meterTypesError) {
      toast.error(String(meterTypesError));
    }
  }, [meterTypesError]);

  useEffect(() => {
    if (slrmTypesError) {
      toast.error(String(slrmTypesError));
    }
  }, [slrmTypesError]);

  const meterTypeLabelById = useMemo(() => {
    const map = {};

    meterTypes.forEach((meterType) => {
      const id = String(
        meterType.wbs_id ?? meterType.id ?? "",
      );

      const label = String(
        meterType.mtr_type ??
          meterType.label ??
          id,
      );

      if (id) {
        map[id] = label;
      }
    });

    return map;
  }, [meterTypes]);

  const slrmTypeLabelById = useMemo(() => {
    const map = {};

    slrmTypes.forEach((slrmType) => {
      const id = String(
        slrmType.wbs_id ?? slrmType.id ?? "",
      );

      const label = String(
        slrmType.mtr_type ??
          slrmType.label ??
          id,
      );

      if (id) {
        map[id] = label;
      }
    });

    return map;
  }, [slrmTypes]);

  // Resolve a selected rate from its saved ID, or from its display name
  // when a list row was populated with the name instead of the ID.
  const resolveTypeId = (savedId, savedValue, options) => {
    const id = String(savedId ?? "").trim();
    const value = String(savedValue ?? "").trim();
    const types = Array.isArray(options) ? options : [];

    const byId = types.find(
      (item) => String(item.wbs_id ?? item.id ?? "").trim() === id,
    );
    if (byId) return String(byId.wbs_id ?? byId.id);

    const byValueId = types.find(
      (item) => String(item.wbs_id ?? item.id ?? "").trim() === value,
    );
    if (byValueId) return String(byValueId.wbs_id ?? byValueId.id);

    const byLabel = types.find(
      (item) =>
        String(item.mtr_type ?? item.label ?? "")
          .trim()
          .toLowerCase() === value.toLowerCase(),
    );
    if (byLabel) return String(byLabel.wbs_id ?? byLabel.id);

    // Preserve a known ID while options are loading; never silently pick
    // the first SLRM type when the saved value cannot be resolved.
    return id || (/^\d+$/.test(value) ? value : "");
  };

  const pageRows = useMemo(() => (Array.isArray(list) ? list : []), [list]);

  const getDateTimestamp = (value) => {
    if (!value) return 0;

    const dateValue = String(value).trim();

    const yyyyMmDd = dateValue.match(
      /^(\d{4})-(\d{2})-(\d{2})/,
    );

    if (yyyyMmDd) {
      return new Date(
        `${yyyyMmDd[1]}-${yyyyMmDd[2]}-${yyyyMmDd[3]}T00:00:00`,
      ).getTime();
    }

    const ddMmYyyy = dateValue.match(
      /^(\d{2})[/-](\d{2})[/-](\d{4})/,
    );

    if (ddMmYyyy) {
      return new Date(
        `${ddMmYyyy[3]}-${ddMmYyyy[2]}-${ddMmYyyy[1]}T00:00:00`,
      ).getTime();
    }

    const timestamp = new Date(value).getTime();

    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  const getYesNoSortValue = (value) => {
    const normalizedValue = String(value || "")
      .trim()
      .toLowerCase();

    return [
      "yes",
      "y",
      "true",
      "1",
      "active",
    ].includes(normalizedValue)
      ? 1
      : 0;
  };

  const getSortableValue = (house, key) => {
    switch (key) {
      case "home_id":
      case "ward_id":
      case "water_balance":
      case "slrm_bal":
      case "balance":
      case "last_reading":
      case "op_water_balance":
      case "op_slrm_bal":
      case "op_balance":
        return Number(house[key] ?? 0);

      case "commercial":
      case "waste_collection":
        return Number(house[key] ?? 0);

      case "is_water":
      case "is_slrm":
      case "slrm_water_together":
      case "status":
        return getYesNoSortValue(house[key]);

      case "last_bill_date":
      case "slrm_last_bill_date":
        return getDateTimestamp(house[key]);

      case "added":
        return getDateTimestamp(
          house.added ||
            house.added_on ||
            house.created_at,
        );

      case "mtr_type":
        return String(
          meterTypeLabelById[
            String(house.mtr_type_id || "")
          ] ||
            house.mtr_type ||
            "",
        )
          .trim()
          .toLowerCase();

      case "slrm_type":
        return String(
          slrmTypeLabelById[
            String(house.slrm_type_id || "")
          ] ||
            house.slrm_type ||
            "",
        )
          .trim()
          .toLowerCase();

      default:
        return String(house[key] ?? "")
          .trim()
          .toLowerCase();
    }
  };

  const sortedRows = useMemo(() => {
    const rows = [...pageRows];

    rows.sort((firstHouse, secondHouse) => {
      const firstValue = getSortableValue(
        firstHouse,
        sortConfig.key,
      );

      const secondValue = getSortableValue(
        secondHouse,
        sortConfig.key,
      );

      if (
        typeof firstValue === "number" &&
        typeof secondValue === "number"
      ) {
        return sortConfig.direction === "asc"
          ? firstValue - secondValue
          : secondValue - firstValue;
      }

      const comparison = String(
        firstValue,
      ).localeCompare(String(secondValue), undefined, {
        numeric: true,
        sensitivity: "base",
      });

      return sortConfig.direction === "asc"
        ? comparison
        : -comparison;
    });

    return rows;
  }, [
    pageRows,
    sortConfig,
    meterTypeLabelById,
    slrmTypeLabelById,
  ]);

  const totalRecords = Number(pagination.total ?? 0);
  const totalPages = Math.max(1, Number(pagination.totalPages ?? 1));
  const startIndex = totalRecords > 0 ? (currentPage - 1) * itemsPerPage : 0;
  const endIndex = Math.min(startIndex + sortedRows.length, totalRecords);
  const currentRows = sortedRows;

  const stats = useMemo(() => {
    const rows = Array.isArray(list) ? list : [];

    const totalHouses = Number(serverStats?.totalHouses ?? pagination.total ?? rows.length);

    const waterConnections = rows.filter(
      (house) =>
        String(house.is_water || "")
          .trim()
          .toLowerCase() === "yes",
    ).length;

    const slrmConnections = rows.filter(
      (house) =>
        String(house.is_slrm || "")
          .trim()
          .toLowerCase() === "yes",
    ).length;

    const activeHouses = rows.filter(
      (house) =>
        String(house.status || "")
          .trim()
          .toLowerCase() === "active",
    ).length;

    const inactiveHouses = rows.filter(
      (house) =>
        String(house.status || "")
          .trim()
          .toLowerCase() === "inactive",
    ).length;

    return {
      totalHouses,
      waterConnections: Number(serverStats?.waterConnections ?? waterConnections),
      slrmConnections: Number(serverStats?.slrmConnections ?? slrmConnections),
      activeHouses: Number(serverStats?.activeHouses ?? activeHouses),
      inactiveHouses: Number(serverStats?.inactiveHouses ?? inactiveHouses),
    };
  }, [list, pagination.total, serverStats]);

  const handleSort = (key) => {
    setSortConfig((previous) => ({
      key,
      direction:
        previous.key === key &&
        previous.direction === "asc"
          ? "desc"
          : "asc",
    }));

    setCurrentPage(1);
  };

  const openCreate = () => {
    if (!canCreate) {
      toast.error("No create permission");
      return;
    }

    setMode("create");

    setForm({
      home_id: null,
      qr_code: "",
      door_no: "",
      family_head: "",
      mobile: "",
      ward_id: "",
      mtr_type: "",
      slrm_type: "",
      is_water: false,
      is_slrm: false,
      commercial: false,
      waste_collection: false,
      last_bill_date: "",
      slrm_last_bill_date: "",
      slrm_water_together: "no",
      status: "Active",
    });

    setShowModal(true);
    dispatch(clearHouseMasterError());
    dispatch(clearWardsError());
  };

  const openEdit = (house) => {
    if (!canUpdate) {
      toast.error("No update permission");
      return;
    }

    const meterTypeId = resolveTypeId(
      house.mtr_type_id,
      house.mtr_type,
      meterTypes,
    );
    const slrmTypeId = resolveTypeId(
      house.slrm_type_id,
      house.slrm_type,
      slrmTypes,
    );

    setMode("edit");

    setForm({
      home_id: house.home_id,
      qr_code: house.qr_code || "",
      door_no: house.door_no || "",
      family_head: house.family_head || "",
      mobile: house.mobile || "",
      ward_id: String(house.ward_id || ""),
      mtr_type: meterTypeId,
      slrm_type: slrmTypeId,
      is_water:
        String(house.is_water || "")
          .toLowerCase() === "yes",
      is_slrm:
        String(house.is_slrm || "")
          .toLowerCase() === "yes",
      commercial:
        Number(house.commercial || 0) === 1,
      waste_collection:
        Number(house.waste_collection || 0) === 1,
      last_bill_date: toInputDate(
        house.last_bill_date,
      ),
      slrm_last_bill_date: toInputDate(
        house.slrm_last_bill_date,
      ),
      slrm_water_together:
        String(
          house.slrm_water_together || "",
        ).toLowerCase() === "yes"
          ? "yes"
          : "no",
      status: house.status || "Active",
    });

    setShowModal(true);
    dispatch(clearHouseMasterError());
    dispatch(clearWardsError());
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    const doorNumber = String(
      form.door_no || "",
    ).trim();

    const wardId = String(
      form.ward_id || "",
    ).trim();

    if (!doorNumber) {
      toast.warning("Door No required");
      return;
    }

    if (!wardId) {
      toast.warning("Ward required");
      return;
    }

    if (mode === "create" && !canCreate) {
      toast.error("No create permission");
      return;
    }

    if (mode === "edit" && !canUpdate) {
      toast.error("No update permission");
      return;
    }

    const payload = {
      qr_code: String(
        form.qr_code || "",
      ).trim(),
      door_no: doorNumber,
      family_head: String(
        form.family_head || "",
      ).trim(),
      mobile: String(
        form.mobile || "",
      ).trim(),
      ward_id: wardId,
      mtr_type: form.mtr_type
        ? String(form.mtr_type)
        : null,
      slrm_type: form.slrm_type
        ? String(form.slrm_type)
        : null,
      is_water: form.is_water
        ? "yes"
        : "no",
      is_slrm: form.is_slrm
        ? "yes"
        : "no",
      commercial: form.commercial
        ? 1
        : 0,
      waste_collection:
        form.waste_collection ? 1 : null,
      status: form.status || "Active",
      last_bill_date:
        form.last_bill_date
          ? String(form.last_bill_date)
          : null,
      slrm_last_bill_date:
        form.slrm_last_bill_date
          ? String(
              form.slrm_last_bill_date,
            )
          : null,
    };

    if (mode === "edit") {
      payload.slrm_water_together =
        form.slrm_water_together || "no";
    }

    if (mode === "create") {
      const result = await dispatch(
        createHouseThunk(payload),
      );

      if (
        createHouseThunk.fulfilled.match(result)
      ) {
        toast.success("House created ✅");
        setShowModal(false);
      } else {
        const message =
          result?.payload?.message ||
          result?.payload ||
          result?.error?.message ||
          "Create failed";

        toast.error(String(message));
      }

      return;
    }

    const result = await dispatch(
      updateHouseThunk({
        home_id: form.home_id,
        ...payload,
      }),
    );

    if (
      updateHouseThunk.fulfilled.match(result)
    ) {
      toast.success("House updated ✅");
      setShowModal(false);
      // Load authoritative type IDs/labels again instead of relying on an
      // optimistic reducer entry which may not include slrm_type_id.
      dispatch(fetchHouseMasterThunk({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm.trim(),
        status: statusFilter === "all" ? "" : statusFilter,
        connection: connectionFilter,
      }));
    } else {
      const message =
        result?.payload?.message ||
        result?.payload ||
        result?.error?.message ||
        "Update failed";

      toast.error(String(message));
    }
  };

  const doDelete = async (house) => {
    const result = await dispatch(
      deleteHouseThunk({
        home_id: house.home_id,
      }),
    );

    if (
      deleteHouseThunk.fulfilled.match(result)
    ) {
      toast.success("House deleted 🗑️");
    } else {
      const message =
        result?.payload?.message ||
        result?.payload ||
        result?.error?.message ||
        "Delete failed";

      toast.error(String(message));
    }
  };

  const onDelete = (house) => {
    if (!canDelete) {
      toast.error("No delete permission");
      return;
    }

    confirmToast(
      `Delete house "${house.door_no}"? This action cannot be undone.`,
      {
        onYes: () => doDelete(house),
      },
    );
  };

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber < 1 ||
      pageNumber > totalPages
    ) {
      return;
    }

    setCurrentPage(pageNumber);
  };

  const StatusBadge = ({ status }) => {
    const isActive =
      String(status || "").toLowerCase() ===
      "active";

    return (
      <Badge
        className={`hm-pill hm-pill-outline ${
          isActive
            ? "is-active"
            : "is-inactive"
        }`}
      >
        {isActive ? "Active" : "Inactive"}
      </Badge>
    );
  };

  const YesNoBadge = ({
    value,
    labelYes = "Yes",
    labelNo = "No",
  }) => {
    const normalizedValue = String(
      value || "",
    ).toLowerCase();

    const isYes =
      normalizedValue === "yes" ||
      normalizedValue === "y" ||
      normalizedValue === "true" ||
      normalizedValue === "1";

    return (
      <Badge
        className={`hm-pill hm-pill-outline ${
          isYes ? "is-yes" : "is-no"
        }`}
      >
        {isYes ? labelYes : labelNo}
      </Badge>
    );
  };

  const Tip = ({ text, children }) => (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{text}</Tooltip>}
    >
      <span className="d-inline-flex">
        {children}
      </span>
    </OverlayTrigger>
  );

  const SortableHeader = ({
    columnKey,
    children,
    className = "",
    style,
  }) => {
    const isActive =
      sortConfig.key === columnKey;

    return (
      <th className={className} style={style}>
        <button
          type="button"
          className={`hm-sort-btn ${
            isActive ? "is-active" : ""
          }`}
          onClick={() =>
            handleSort(columnKey)
          }
          title={`Sort by ${children}`}
        >
          <span>{children}</span>

          {isActive ? (
            sortConfig.direction === "asc" ? (
              <ChevronUp size={14} />
            ) : (
              <ChevronDown size={14} />
            )
          ) : (
            <ChevronsUp size={14} />
          )}
        </button>
      </th>
    );
  };

  const renderPagination = () => {
    const maxVisiblePages = 5;

    let startPage = Math.max(
      1,
      currentPage - Math.floor(maxVisiblePages / 2),
    );

    let endPage = Math.min(
      totalPages,
      startPage + maxVisiblePages - 1,
    );

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(
        1,
        endPage - maxVisiblePages + 1,
      );
    }

    const pageNumbers = [];

    for (
      let pageNumber = startPage;
      pageNumber <= endPage;
      pageNumber += 1
    ) {
      pageNumbers.push(pageNumber);
    }

    return (
      <div className="hm-pagination-bar">
        <div className="hm-pagination-info">
          Showing{" "}
          <span className="fw-bold">
            {totalRecords ? startIndex + 1 : 0}
          </span>{" "}
          to{" "}
          <span className="fw-bold">
            {endIndex}
          </span>{" "}
          of{" "}
          <span className="fw-bold">
            {totalRecords}
          </span>
        </div>

        <Pagination className="hm-pagination mb-0">
          <Pagination.Item
            className="hm-pagination-nav"
            onClick={() =>
              handlePageChange(currentPage - 1)
            }
            disabled={currentPage === 1}
          >
            Prev
          </Pagination.Item>

          {pageNumbers.map((pageNumber) => (
            <Pagination.Item
              key={pageNumber}
              active={pageNumber === currentPage}
              onClick={() =>
                handlePageChange(pageNumber)
              }
            >
              {pageNumber}
            </Pagination.Item>
          ))}

          <Pagination.Item
            className="hm-pagination-nav"
            onClick={() =>
              handlePageChange(currentPage + 1)
            }
            disabled={currentPage === totalPages}
          >
            Next
          </Pagination.Item>
        </Pagination>
      </div>
    );
  };

  return (
    <div className="container-fluid px-3 py-4 hm-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1 fw-bold hm-title">
            House Master
          </h4>
        </div>

        <Tip
          text={
            !canCreate
              ? "No create permission"
              : "Add new house"
          }
        >
          <Button
            onClick={openCreate}
            disabled={!canCreate}
            className="hm-primary-btn d-flex align-items-center gap-2"
            style={{
              background: "#2563eb",
              borderColor: "#2563eb",
            }}
          >
            <Plus size={18} />
            Add House
          </Button>
        </Tip>
      </div>

      <Row className="g-3 mb-4">
        <Col md={6} xl={3}>
          <Card className="hm-card-soft h-100">
            <Card.Body className="py-3">
              <div className="d-flex align-items-center gap-3">
                <div className="hm-stat-icon">
                  <Home size={18} />
                </div>

                <div>
                  <div className="text-muted small">
                    Total Houses
                  </div>

                  <div className="fs-3 fw-bold">
                    {stats.totalHouses}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} xl={3}>
          <Card className="hm-card-soft h-100">
            <Card.Body className="py-3">
              <div className="d-flex align-items-center gap-3">
                <div className="hm-stat-icon cyan">
                  <Users size={18} />
                </div>

                <div>
                  <div className="text-muted small">
                    Water Connections
                  </div>

                  <div className="fs-3 fw-bold">
                    {stats.waterConnections}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} xl={2}>
          <Card className="hm-card-soft h-100">
            <Card.Body className="py-3">
              <div className="d-flex align-items-center gap-3">
                <div className="hm-stat-icon green">
                  <CheckCircle size={18} />
                </div>

                <div>
                  <div className="text-muted small">
                    Slrm Connections
                  </div>

                  <div className="fs-3 fw-bold">
                    {stats.slrmConnections}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} xl={2}>
          <Card className="hm-card-soft h-100">
            <Card.Body className="py-3">
              <div className="d-flex align-items-center gap-3">
                <div className="hm-stat-icon green">
                  <CheckCircle size={18} />
                </div>

                <div>
                  <div className="text-muted small">
                    Active Houses
                  </div>

                  <div className="fs-3 fw-bold">
                    {stats.activeHouses}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} xl={2}>
          <Card className="hm-card-soft h-100">
            <Card.Body className="py-3">
              <div className="d-flex align-items-center gap-3">
                <div className="hm-stat-icon gray">
                  <XCircle size={18} />
                </div>

                <div>
                  <div className="text-muted small">
                    Inactive Houses
                  </div>

                  <div className="fs-3 fw-bold">
                    {stats.inactiveHouses}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="hm-card">
        <Card.Body className="p-0">
          <div className="hm-table-toolbar">
            <Row className="g-3 align-items-end">
              <Col lg={5} md={12}>
                <Form.Label className="hm-filter-label">Search houses</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="hm-inputgroup-text">
                    <Search size={18} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    value={searchTerm}
                    onChange={(event) => {
                      setSearchTerm(event.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="House code, door no, family head, mobile, ward..."
                    className="hm-input hm-inputgroup-control"
                  />
                </InputGroup>
              </Col>

              <Col lg={2} md={4} sm={6}>
                <Form.Label className="hm-filter-label">Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(event) => {
                    setStatusFilter(event.target.value);
                    setCurrentPage(1);
                  }}
                  className="hm-select"
                >
                  <option value="all">All</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Form.Select>
              </Col>

              <Col lg={5} md={8}>
                <Form.Label className="hm-filter-label">Connection</Form.Label>
                <div className="hm-connection-filters">
                  {[
                    { value: "water", label: "Water" },
                    { value: "slrm", label: "SLRM" },
                    { value: "both", label: "Both" },
                    { value: "none", label: "None" },
                  ].map((option) => (
                    <Form.Check
                      key={option.value}
                      type="checkbox"
                      id={`connection-${option.value}`}
                      label={option.label}
                      checked={connectionFilter === option.value}
                      onChange={() => {
                        setConnectionFilter((previous) =>
                          previous === option.value ? "" : option.value,
                        );
                        setCurrentPage(1);
                      }}
                      className="hm-connection-check"
                    />
                  ))}
                </div>
              </Col>
            </Row>

            <div className="hm-table-toolbar-bottom">
              <div className="hm-result-count">
                {loading
                  ? "Loading..."
                  : `${totalRecords} house${totalRecords === 1 ? "" : "s"} found`}
              </div>

              <div className="hm-toolbar-actions">
                <div className="hm-rows-control">
                  <span className="hm-rows-label">
                    Rows per page
                  </span>

                  <Form.Select
                    value={itemsPerPage}
                    onChange={(event) => {
                      setItemsPerPage(Number(event.target.value));
                      setCurrentPage(1);
                    }}
                    className="hm-page-size-select"
                    aria-label="Rows per page"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </div>

                {(searchTerm ||
                  statusFilter !== "all" ||
                  connectionFilter) && (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    className="hm-clear-filter-btn"
                    onClick={() => {
                      setSearchTerm("");
                      setStatusFilter("all");
                      setConnectionFilter("");
                      setCurrentPage(1);
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />

              <div className="mt-3 text-muted">
                Loading houses...
              </div>
            </div>
          ) : (
            <>
              <div className="hm-table-wrap">
                <Table
                  hover
                  className="hm-table"
                >
                  <thead>
                    <tr>
                      <SortableHeader
                        columnKey="home_id"
                        style={{ width: 70 }}
                      >
                        ID
                      </SortableHeader>

                      <SortableHeader
                        columnKey="qr_code"
                        style={{ width: 140 }}
                      >
                        House Code
                      </SortableHeader>

                      <SortableHeader
                        columnKey="family_head"
                        style={{ width: 180 }}
                      >
                        Family Head
                      </SortableHeader>

                      <SortableHeader
                        columnKey="door_no"
                        style={{ width: 120 }}
                      >
                        Door No
                      </SortableHeader>

                      <SortableHeader
                        columnKey="mobile"
                        style={{ width: 140 }}
                      >
                        Mobile
                      </SortableHeader>

                      <SortableHeader
                        columnKey="ward_name"
                        style={{ width: 160 }}
                      >
                        Ward
                      </SortableHeader>

                      <SortableHeader
                        columnKey="mtr_type"
                        style={{ width: 160 }}
                      >
                        Water Rate
                      </SortableHeader>

                      <SortableHeader
                        columnKey="slrm_type"
                        style={{ width: 160 }}
                      >
                        Slrm Rate
                      </SortableHeader>

                      <SortableHeader
                        columnKey="is_water"
                        style={{ width: 80 }}
                      >
                        Water
                      </SortableHeader>

                      <SortableHeader
                        columnKey="is_slrm"
                        style={{ width: 80 }}
                      >
                        SLRM
                      </SortableHeader>

                      <SortableHeader
                        columnKey="commercial"
                        style={{ width: 110 }}
                      >
                        Commercial
                      </SortableHeader>

                      <SortableHeader
                        columnKey="waste_collection"
                        style={{ width: 140 }}
                      >
                        Waste Collection
                      </SortableHeader>

                      <SortableHeader
                        columnKey="status"
                        style={{ width: 90 }}
                      >
                        Status
                      </SortableHeader>

                      <SortableHeader
                        columnKey="water_balance"
                        style={{ width: 130 }}
                      >
                        Water Bal
                      </SortableHeader>

                      <SortableHeader
                        columnKey="slrm_bal"
                        style={{ width: 120 }}
                      >
                        Slrm Bal
                      </SortableHeader>

                      <SortableHeader
                        columnKey="balance"
                        style={{ width: 110 }}
                      >
                        Balance
                      </SortableHeader>

                      <SortableHeader
                        columnKey="last_reading"
                        style={{ width: 120 }}
                      >
                        Last Reading
                      </SortableHeader>

                      <SortableHeader
                        columnKey="last_bill_date"
                        style={{ width: 150 }}
                      >
                        Water Last Bill Date
                      </SortableHeader>

                      <SortableHeader
                        columnKey="slrm_last_bill_date"
                        style={{ width: 150 }}
                      >
                        Slrm Last Bill Date
                      </SortableHeader>

                      <SortableHeader
                        columnKey="op_water_balance"
                        style={{ width: 130 }}
                      >
                        OP Water
                      </SortableHeader>

                      <SortableHeader
                        columnKey="op_slrm_bal"
                        style={{ width: 120 }}
                      >
                        OP SLRM
                      </SortableHeader>

                      <SortableHeader
                        columnKey="op_balance"
                        style={{ width: 130 }}
                      >
                        OP Total Bal
                      </SortableHeader>

                      <SortableHeader
                        columnKey="slrm_water_together"
                        style={{ width: 170 }}
                      >
                        Bill Together
                      </SortableHeader>

                      <SortableHeader
                        columnKey="dist_name"
                        style={{ width: 140 }}
                      >
                        District
                      </SortableHeader>

                      <SortableHeader
                        columnKey="taluk_name"
                        style={{ width: 140 }}
                      >
                        Taluk
                      </SortableHeader>

                      <SortableHeader
                        columnKey="pan_name"
                        style={{ width: 160 }}
                      >
                        Panchayath
                      </SortableHeader>

                      <SortableHeader
                        columnKey="added"
                        style={{ width: 130 }}
                      >
                        Added On
                      </SortableHeader>

                      <th
                        className="text-end hm-sticky-actions"
                        style={{ width: 140 }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentRows.length === 0 ? (
                      <tr>
                        <td colSpan={28}>
                          <div className="hm-empty text-center">
                            <div className="hm-empty-icon mx-auto">
                              <Search size={22} />
                            </div>

                            <div className="fw-bold mb-1">
                              No houses found
                            </div>

                            <div className="text-muted mb-3">
                              Try changing search or
                              filters.
                            </div>

                            {(searchTerm ||
                              statusFilter !== "all" ||
                              connectionFilter) && (
                              <Button
                                variant="outline-primary"
                                className="hm-action-btn"
                                onClick={() => {
                                  setSearchTerm("");
                                  setStatusFilter("all");
                                  setConnectionFilter("");
                                  setCurrentPage(1);
                                }}
                              >
                                Clear Filters
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      currentRows.map(
                        (house) => {
                          const meterLabel =
                            meterTypeLabelById[
                              String(
                                house.mtr_type_id ||
                                  "",
                              )
                            ] ||
                            house.mtr_type ||
                            "—";

                          const slrmLabel =
                            slrmTypeLabelById[
                              String(
                                house.slrm_type_id ||
                                  "",
                              )
                            ] ||
                            house.slrm_type ||
                            "—";

                          return (
                            <tr
                              key={
                                house.home_id
                              }
                            >
                              <td className="text-muted fw-semibold">
                                #
                                {house.home_id}
                              </td>

                              <td>
                                {house.qr_code ||
                                  "—"}
                              </td>

                              <td>
                                {house.family_head || (
                                  <span className="text-muted">
                                    —
                                  </span>
                                )}
                              </td>

                              <td>
                                <div className="fw-bold">
                                  {house.door_no ||
                                    "—"}
                                </div>
                              </td>

                              <td>
                                {house.mobile || (
                                  <span className="text-muted">
                                    —
                                  </span>
                                )}
                              </td>

                              <td>
                                <div className="fw-semibold">
                                  {house.ward_name ||
                                    "—"}
                                </div>

                                <div className="text-muted small">
                                  #
                                  {house.ward_id ||
                                    "—"}
                                </div>
                              </td>

                              <td>
                                {meterLabel}
                              </td>

                              <td>
                                {slrmLabel}
                              </td>

                              <td>
                                <YesNoBadge
                                  value={
                                    house.is_water
                                  }
                                />
                              </td>

                              <td>
                                <YesNoBadge
                                  value={
                                    house.is_slrm
                                  }
                                />
                              </td>

                              <td>
                                <YesNoBadge
                                  value={
                                    Number(
                                      house.commercial ||
                                        0,
                                    ) === 1
                                      ? "yes"
                                      : "no"
                                  }
                                />
                              </td>

                              <td>
                                <YesNoBadge
                                  value={
                                    Number(
                                      house.waste_collection ||
                                        0,
                                    ) === 1
                                      ? "yes"
                                      : "no"
                                  }
                                />
                              </td>

                              <td>
                                <StatusBadge
                                  status={
                                    house.status
                                  }
                                />
                              </td>

                              <td>
                                {fmtNum(
                                  house.water_balance,
                                )}
                              </td>

                              <td>
                                {fmtNum(
                                  house.slrm_bal,
                                )}
                              </td>

                              <td>
                                {fmtNum(
                                  house.balance,
                                )}
                              </td>

                              <td>
                                {house.last_reading ??
                                  "—"}
                              </td>

                              <td>
                                {fmtDate(
                                  house.last_bill_date,
                                )}
                              </td>

                              <td>
                                {fmtDate(
                                  house.slrm_last_bill_date,
                                )}
                              </td>

                              <td>
                                {fmtNum(
                                  house.op_water_balance,
                                )}
                              </td>

                              <td>
                                {fmtNum(
                                  house.op_slrm_bal,
                                )}
                              </td>

                              <td>
                                {fmtNum(
                                  house.op_balance,
                                )}
                              </td>

                              <td>
                                <YesNoBadge
                                  value={
                                    house.slrm_water_together
                                  }
                                  labelYes="Yes"
                                  labelNo="No"
                                />
                              </td>

                              <td>
                                {house.dist_name ||
                                  "—"}
                              </td>

                              <td>
                                {house.taluk_name ||
                                  "—"}
                              </td>

                              <td>
                                {house.pan_name ||
                                  "—"}
                              </td>

                              <td>
                                {fmtDate(
                                  house.added ||
                                    house.added_on ||
                                    house.created_at,
                                )}
                              </td>

                              <td className="text-end hm-sticky-actions">
                                <div className="d-inline-flex gap-2">
                                  <Tip
                                    text={
                                      canView
                                        ? "View"
                                        : "No view permission"
                                    }
                                  >
                                    <Button
                                      variant="light"
                                      className="hm-icon-btn primary"
                                      disabled={
                                        !canView
                                      }
                                      onClick={() =>
                                        navigate(
                                          `/house-profile/${house.home_id}`,
                                        )
                                      }
                                    >
                                      <Eye
                                        size={16}
                                      />
                                    </Button>
                                  </Tip>

                                  <Tip
                                    text={
                                      canUpdate
                                        ? "Edit"
                                        : "No update permission"
                                    }
                                  >
                                    <Button
                                      variant="light"
                                      className="hm-icon-btn success"
                                      disabled={
                                        !canUpdate ||
                                        saving
                                      }
                                      onClick={() =>
                                        openEdit(
                                          house,
                                        )
                                      }
                                    >
                                      <Edit2
                                        size={16}
                                      />
                                    </Button>
                                  </Tip>

                                  <Tip
                                    text={
                                      canDelete
                                        ? "Delete"
                                        : "No delete permission"
                                    }
                                  >
                                    <Button
                                      variant="light"
                                      className="hm-icon-btn danger"
                                      disabled={
                                        !canDelete ||
                                        saving
                                      }
                                      onClick={() =>
                                        onDelete(
                                          house,
                                        )
                                      }
                                    >
                                      <Trash2
                                        size={16}
                                      />
                                    </Button>
                                  </Tip>
                                </div>
                              </td>
                            </tr>
                          );
                        },
                      )
                    )}
                  </tbody>
                </Table>
              </div>

              {currentRows.length > 0 && (
                <div className="hm-pagination-footer">
                  {renderPagination()}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        size="lg"
        className="hm-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            {mode === "create"
              ? "Add New House"
              : "Edit House Details"}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={onSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Door Number{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Form.Label>

                  <Form.Control
                    value={form.door_no}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        door_no:
                          event.target.value,
                      }))
                    }
                    placeholder="e.g., 12-A, Block 4"
                    required
                    className="hm-input"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    QR Code
                  </Form.Label>

                  <Form.Control
                    value={form.qr_code}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        qr_code:
                          event.target.value,
                      }))
                    }
                    placeholder="Enter QR code if available"
                    className="hm-input"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Family Head
                  </Form.Label>

                  <Form.Control
                    value={form.family_head}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        family_head:
                          event.target.value,
                      }))
                    }
                    placeholder="Name of family head"
                    className="hm-input"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Mobile Number
                  </Form.Label>

                  <Form.Control
                    value={form.mobile}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        mobile:
                          event.target.value,
                      }))
                    }
                    placeholder="10-digit mobile number"
                    className="hm-input"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Last Water Bill Date
                  </Form.Label>

                  <Form.Control
                    type="date"
                    value={
                      form.last_bill_date || ""
                    }
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        last_bill_date:
                          event.target.value,
                      }))
                    }
                    className="hm-input"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Last Slrm Bill Date
                  </Form.Label>

                  <Form.Control
                    type="date"
                    value={
                      form.slrm_last_bill_date ||
                      ""
                    }
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        slrm_last_bill_date:
                          event.target.value,
                      }))
                    }
                    className="hm-input"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Ward{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Form.Label>

                  <Form.Select
                    value={form.ward_id}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        ward_id:
                          event.target.value,
                      }))
                    }
                    required
                    className="hm-select"
                    disabled={wardsLoading}
                  >
                    <option value="">
                      {wardsLoading
                        ? "Loading wards..."
                        : "Select Ward"}
                    </option>

                    {wards.map((ward) => (
                      <option
                        key={String(
                          ward.ward_id,
                        )}
                        value={String(
                          ward.ward_id,
                        )}
                      >
                        {ward.ward_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Meter Type
                  </Form.Label>

                  <Form.Select
                    value={form.mtr_type}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        mtr_type:
                          event.target.value,
                      }))
                    }
                    className="hm-select"
                    disabled={
                      meterTypesLoading
                    }
                  >
                    <option value="">
                      {meterTypesLoading
                        ? "Loading meter types..."
                        : "Select meter type"}
                    </option>

                    {meterTypes.map(
                      (meterType) => (
                        <option
                          key={String(
                            meterType.wbs_id,
                          )}
                          value={String(
                            meterType.wbs_id,
                          )}
                        >
                          {
                            meterType.mtr_type
                          }
                        </option>
                      ),
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Slrm Type
                  </Form.Label>

                  <Form.Select
                    value={form.slrm_type}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        slrm_type:
                          event.target.value,
                      }))
                    }
                    className="hm-select"
                    disabled={
                      slrmTypesLoading
                    }
                  >
                    <option value="">
                      {slrmTypesLoading
                        ? "Loading slrm types..."
                        : "Select SLRM type"}
                    </option>

                    {form.slrm_type &&
                      !slrmTypes.some(
                        (item) => String(item.wbs_id) === String(form.slrm_type),
                      ) && (
                        <option value={String(form.slrm_type)} disabled>
                          Saved SLRM type (ID: {form.slrm_type}) - not in current options
                        </option>
                      )}

                    {slrmTypes.map(
                      (slrmType) => (
                        <option
                          key={String(
                            slrmType.wbs_id,
                          )}
                          value={String(
                            slrmType.wbs_id,
                          )}
                        >
                          {
                            slrmType.mtr_type
                          }
                        </option>
                      ),
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>

              {mode === "edit" && (
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">
                      Slrm + Water Together
                    </Form.Label>

                    <Form.Select
                      value={
                        form.slrm_water_together
                      }
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          slrm_water_together:
                            event.target.value,
                        }))
                      }
                      className="hm-select"
                    >
                      <option value="no">
                        No
                      </option>

                      <option value="yes">
                        Yes
                      </option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}

              <Col md={6}>
                <div
                  className="border rounded-4 p-3"
                  style={{
                    borderColor:
                      "rgba(15,23,42,0.10)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-bold">
                        Water Connection
                      </div>

                      <div className="text-muted small">
                        House has water
                        connection?
                      </div>
                    </div>

                    <Form.Check
                      type="switch"
                      checked={form.is_water}
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          is_water:
                            event.target.checked,
                        }))
                      }
                    />
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div
                  className="border rounded-4 p-3"
                  style={{
                    borderColor:
                      "rgba(15,23,42,0.10)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-bold">
                        Slrm Collection
                      </div>

                      <div className="text-muted small">
                        Slrm waste collection
                        enabled?
                      </div>
                    </div>

                    <Form.Check
                      type="switch"
                      checked={form.is_slrm}
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          is_slrm:
                            event.target.checked,
                        }))
                      }
                    />
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div
                  className="border rounded-4 p-3"
                  style={{
                    borderColor:
                      "rgba(15,23,42,0.10)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-bold">
                        Commercial
                      </div>

                      <div className="text-muted small">
                        Mark this house as a
                        commercial property
                      </div>
                    </div>

                    <Form.Check
                      type="switch"
                      checked={form.commercial}
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          commercial:
                            event.target.checked,
                        }))
                      }
                    />
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div
                  className="border rounded-4 p-3"
                  style={{
                    borderColor:
                      "rgba(15,23,42,0.10)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-bold">
                        Waste Collection
                      </div>

                      <div className="text-muted small">
                        Enable waste collection
                        service for this house
                      </div>
                    </div>

                    <Form.Check
                      type="switch"
                      checked={
                        form.waste_collection
                      }
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          waste_collection:
                            event.target.checked,
                        }))
                      }
                    />
                  </div>
                </div>
              </Col>

             <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Status
                  </Form.Label>

                  <Form.Select
                    value={form.status}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        status: event.target.value,
                      }))
                    }
                    className="hm-select"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="light"
              onClick={closeModal}
              disabled={saving}
              className="hm-dropdown-btn"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={
                saving ||
                !String(
                  form.door_no || "",
                ).trim() ||
                !String(
                  form.ward_id || "",
                ).trim() ||
                (mode === "create" &&
                  !canCreate) ||
                (mode === "edit" &&
                  !canUpdate)
              }
              className="hm-primary-btn"
              style={{
                background: "#2563eb",
                borderColor: "#2563eb",
              }}
            >
              {saving ? (
                <>
                  <Spinner
                    size="sm"
                    animation="border"
                    className="me-2"
                  />

                  {mode === "create"
                    ? "Creating..."
                    : "Updating..."}
                </>
              ) : mode === "create" ? (
                "Create House"
              ) : (
                "Update House"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
